import React from "react";
import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {changePageSizeState} from "../../../Slices/PageSlice";
import PageSizeState from "../../../Constants/Base/PageSizeState";
import {useDispatch} from "react-redux";
import {deletePartner, getPartners} from "../../../Services/CRM/PartnerService";
import TablePage from "../../../Components/DataGrid/TablePage";
import strings from "../../../localization";
import {formatColumnObject} from "../../../Components/DataGrid/ValueFormatter";
import {changeBreadcrumbs} from "../../../Slices/BreadcrumbsSlice";
import Modules from "../../../Constants/Base/Modules";
import AddPartner from "./AddPartner";
import TablePageContext, {
    FilterDefaults,
    TableDataDefaults,
    TablePageOptionDefaults
} from "../../../Context/TablePageContext";
import PageState from "../../../Constants/Base/PageState";
import EditPartner from "./EditPartner";
import ReferenceType from "../../../Constants/DocumentManagement/ReferenceType";
import {useNavigate, useSearchParams} from 'react-router-dom';
import { getCountries, getPartnerTypes } from '../../../Util/SiteDataUtil';
import AppPermissions from "../../../Constants/Permissions/Permissions";
import {getPartnerLegalTypeString} from "../../../Constants/CRM/PartnerLegalType";
import Tag from "../../../Components/Forms/Pages/Document/Tag/Tag";

const tableDescription = [
    {field: 'name', headerName: strings.pages.crm.partner.partnerList.name, width: 250},
    {
        field: 'partnerLegalType',
        headerName: strings.pages.crm.partner.partnerList.partnerLegalType,
        valueFormatter: (params) => formatColumnPartnerLegalType(params),
        width: 120
    },
    {field: 'description', headerName: strings.pages.crm.partner.partnerList.description, width: 250},
    {field: 'account', headerName: strings.pages.crm.partner.partnerList.account, width: 200},
    {field: 'registrationNumber', headerName: strings.pages.crm.partner.partnerList.registrationNumber, width: 200},
    {field: 'taxNumber', headerName: strings.pages.crm.partner.partnerList.taxNumber, width: 200},
    {field: 'website', headerName: strings.pages.crm.partner.partnerList.website, width: 200},

    {
        field: 'partnerType',
        headerName: strings.pages.crm.partner.partnerList.partnerType,
        valueFormatter: (params) => formatColumnObject(params, 'name'),
        width: 120
    },
    {
        field: 'country',
        headerName: strings.pages.crm.partner.partnerList.country,
        valueFormatter: (params) => formatColumnObject(params, 'name'),
        width: 150
    },
    {field: 'city', headerName: strings.pages.crm.partner.partnerList.city},
    {field: 'address', headerName: 'Address', width: 200},
    {
        field: 'currency',
        headerName: strings.pages.crm.partner.partnerList.currency,
        valueFormatter: (params) => formatColumnObject(params, 'code'),
        width: 100
    },
];

const filters = [
    {
        name: 'partnerType',
        nameKey: 'name',
        valueKey: 'id',
        optionsName: 'partnerType',
        label: strings.pages.crm.partner.partnerList.partnerType,
        showSelect: true
    },
    {
        name: 'country',
        nameKey: 'name',
        valueKey: 'id',
        optionsName: 'country',
        label: strings.pages.crm.partner.partnerList.country
    }
]

const formatColumnPartnerLegalType = (params) => {

    if(!params.value) {
        return ''
    }

    return getPartnerLegalTypeString(params.value)
}

const PartnerList = (props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [updatedFilter, setUpdatedFilter] = useState(!Boolean(searchParams.toString()));
    const siteData = useSelector((state) => state.siteData);
    const [columns, setColumns] = useState([]);
    const [pageState, setPageState] = useState(PageState.View);
    const [filter, setFilter] = useState(FilterDefaults);
    const [tableData, setTableData] = useState(TableDataDefaults);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectionModel, setSelectionModel] = useState([]);
    const [selectedItems, setSelectedItems] = useState(null);
    const [tablePageOptions, setTablePageOptions] = useState(TablePageOptionDefaults);
    const [filterOptions, setFilterOptions] = useState({});
    const referenceType = ReferenceType.PARTNER;
    const permissionGroup = AppPermissions.PARTNER.GROUP;

    const value = {
        tablePageOptions, setTablePageOptions,
        navigate,
        selectionModel, setSelectionModel,
        selectedItems, setSelectedItems,
        filter, setFilter, pageState, setPageState, showDeleteDialog, setShowDeleteDialog,
        updatedFilter, setUpdatedFilter
    }


    useEffect(() => {
        setColumns([...tableDescription]);
        dispatch(changePageSizeState(PageSizeState.SHORT));
        dispatch(changeBreadcrumbs({
            title: strings.pages.crm.partner.partnerList.pageTitle,
            hierarchy:[
                {label: strings.navigation.managmentTag},
                {label: Modules.CRM},
                {label: strings.pages.crm.partner.partnerList.pageTitle},
            ],        
        }));

        return () => {
            setTableData({})
        }
    }, [])

    useEffect(() => {
        if(!updatedFilter){
            return;
        }

        fetch();
    }, [filter, updatedFilter])

    useEffect(() => {
        if(!Object.keys(siteData.data).length){
            return;
        }

        setFilterOptions({
            country: getCountries(siteData),
            partnerType: getPartnerTypes(siteData),
        })
    },[siteData])

    const onCancel = () => {
        setPageState(PageState.View);
    }

    const onFinish = () => {
        fetch();
        setPageState(PageState.View);
    }

    const fetch = () => {
        setTableData({
            ...tableData,
            loading: true
        });

        getPartners({
            ...filter
        }).then(response => {

            if (!response || !response.ok) {
                return;
            }
            
            setTableData({
                loading: false,
                data: response.data.result,
                total: response.data.total,
            });
        })
    }

    return <TablePageContext.Provider value={value}>
        <TablePage onFinish={() => onFinish()} deleteItem={deletePartner} tableDescription={columns}
                   tableData={tableData} 
                   filter={filter} filters={filters} filterOptions={filterOptions}
                   permissionGroup={permissionGroup}
                   referenceType={referenceType}
                   tagPage={<Tag referenceType={ referenceType } data={ selectedItems } /> }
                   editPage={<EditPartner data={ selectedItems }
                                          onCancel={() => onCancel()} onFinish={() => onFinish()}/>}
                   addPage={<AddPartner onCancel={() => onCancel()} onFinish={() => onFinish()}/>}
                   addButtonText={strings.components.tablePage.addButton.partner}/>
    </TablePageContext.Provider>;
}

export default PartnerList;
